
import { useState } from "react";
import { ProfileData } from "@/types/profile";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useProfileStorage } from "./useProfileStorage";
import { useProfileSaving } from "./useProfileSaving";

/**
 * Hook for handling profile form events and validation
 */
export const useProfileFormHandlers = (
  profile: ProfileData,
  setProfile: React.Dispatch<React.SetStateAction<ProfileData>>,
  setUploading: (uploading: boolean) => void
) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { saveToStorage } = useProfileStorage();
  const [isFormDirty, setIsFormDirty] = useState(false);
  
  // Get saving functionality from the hook
  const { savingState, autoSaved, saveProfile } = useProfileSaving(profile, setUploading);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Update profile state
    setProfile(prev => ({ ...prev, [name]: value }));
    
    // Update local storage to preserve changes on refresh
    saveToStorage({
      ...profile,
      [name]: value
    });
    
    // Mark form as dirty when changes are made
    setIsFormDirty(true);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      console.error("Cannot save profile: No authenticated user");
      return false;
    }
    
    return await saveProfile();
  };

  // Handle image upload
  const handleImageUpload = (url: string) => {
    setProfile(prev => ({ ...prev, avatar_url: url }));
    
    // Save to storage
    saveToStorage({
      ...profile,
      avatar_url: url
    });
    
    // Mark form as dirty when changes are made
    setIsFormDirty(true);
  };

  return {
    handleChange,
    handleSubmit,
    handleImageUpload,
    autoSaved,
    savingState,
    isFormDirty
  };
};
